import { useState } from 'react';
import { Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { DashboardData } from '../../../services/dashboard';
import {
  TOP_CHART_CARD_HEIGHT,
  cardBodySx,
  cardDividerSx,
  cardHeaderSx,
  cardSubtitleSx,
  cardTitleSx,
  chartCardSx,
  chartTooltipProps,
  checkboxControlSx,
  checkboxLabelSx,
} from './dashboardCardStyles';

type DonutComponentProps = {
  enquiries: DashboardData['enquiries'];
};

export const DonutComponent = ({ enquiries }: DonutComponentProps) => {
  const [showChart, setShowChart] = useState(true);
  const [showValue, setShowValue] = useState(true);

  const items = [
    {
      key: 'new',
      data: enquiries.new,
      colors: ['#FF5B5B', '#3f2727'] as [string, string],
      label: 'New Enquires',
    },
    {
      key: 'inProgress',
      data: enquiries.inProgress,
      colors: ['#407BFF', '#232b3f'] as [string, string],
      label: 'In Progress',
    },
    {
      key: 'completed',
      data: enquiries.completed,
      colors: ['#00B074', '#183229'] as [string, string],
      label: 'Completed',
    },
  ];

  const cancelledCount = enquiries.cancelled?.count ?? 0;

  return (
    <Box sx={chartCardSx(TOP_CHART_CARD_HEIGHT)}>
      <Box sx={cardHeaderSx}>
        <Box>
          <Typography sx={cardTitleSx}>All Enquires</Typography>
          <Typography sx={cardSubtitleSx}>
            {enquiries.total.toLocaleString('en-IN')} total
            {cancelledCount > 0 ? ` · ${cancelledCount} cancelled` : ''}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <FormControlLabel
            sx={checkboxLabelSx}
            control={
              <Checkbox
                size="small"
                checked={showChart}
                onChange={(e) => setShowChart(e.target.checked)}
                sx={checkboxControlSx}
              />
            }
            label="Chart"
          />
          <FormControlLabel
            sx={checkboxLabelSx}
            control={
              <Checkbox
                size="small"
                checked={showValue}
                onChange={(e) => setShowValue(e.target.checked)}
                sx={checkboxControlSx}
              />
            }
            label="%"
          />
        </Box>
      </Box>

      <Box sx={{ ...cardDividerSx, ...cardBodySx, justifyContent: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            width: '100%',
            gap: 1,
          }}
        >
          {items.map((item) => {
            const centerText = showValue
              ? `${item.data.percent}%`
              : item.data.count.toLocaleString('en-IN');

            const tooltipTitle = (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{item.label}</Typography>
                <Typography sx={{ fontSize: 12, color: '#ccc' }}>
                  Count: {item.data.count.toLocaleString('en-IN')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                  Share: {item.data.percent}% of total
                </Typography>
              </Box>
            );

            return showChart ? (
              <Tooltip key={item.key} title={tooltipTitle} {...chartTooltipProps}>
                <Box sx={{ cursor: 'default' }}>
                  <DonutChart
                    percentage={item.data.percent}
                    colors={item.colors}
                    label={item.label}
                    centerText={centerText}
                  />
                </Box>
              </Tooltip>
            ) : (
              <Tooltip key={item.key} title={tooltipTitle} {...chartTooltipProps}>
                <Box sx={{ textAlign: 'center', minWidth: 90, cursor: 'default' }}>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, color: item.colors[0], lineHeight: 1 }}>
                    {item.data.count.toLocaleString('en-IN')}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#8A8D91', mt: 1.25 }}>{item.label}</Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const DonutChart = ({
  percentage,
  colors,
  label,
  centerText,
  radius = 54,
  strokeWidth = 15,
}: {
  percentage: number;
  colors: [string, string];
  label: string;
  centerText: string;
  radius?: number;
  strokeWidth?: number;
}) => {
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - (circumference * percentage) / 100;

  return (
    <Box sx={{ textAlign: 'center', flex: '1 1 0', maxWidth: 128 }}>
      <Box sx={{ display: 'inline-flex', justifyContent: 'center' }}>
        <svg height={radius * 2} width={radius * 2} style={{ display: 'block' }}>
          <circle
            stroke={colors[1]}
            fill="none"
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            strokeWidth={strokeWidth}
          />
          <circle
            stroke={colors[0]}
            fill="none"
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${radius} ${radius})`}
            style={{ transition: 'stroke-dashoffset 0.45s ease' }}
          />
          <circle fill="white" cx={radius} cy={radius} r={radius - strokeWidth - 1} />
          <text
            x={radius}
            y={radius}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={radius / 2.6}
            fontWeight="700"
            fill="#111"
          >
            {centerText}
          </text>
        </svg>
      </Box>
      <Typography sx={{ color: '#8A8D91', fontSize: 12, mt: 1.25, lineHeight: 1.2 }}>{label}</Typography>
    </Box>
  );
};
